// @file Composable to encapsulate post duplicate and transfer logic

import { trackEvent } from '@@/bits/analytics'
import { isAppCapableOf } from '@@/bits/app_can'
import device from '@@/bits/device'
import { ALERT_SIGN_EMOJI } from '@@/bits/emoji'
import { isAppUsing } from '@@/bits/flip'
import { removeHashidPrefix } from '@@/bits/hashid_helper'
import { __ } from '@@/bits/intl'
import { navigateTo } from '@@/bits/location'
import { hasCustomProperties } from '@@/bits/post_properties'
import type { PostGroupingInfo } from '@@/bits/surface_posts_grouping_helper'
import { WallWishGroupByTypes, WallWishGroupByValue } from '@@/enums'
import { showAppNotification } from '@@/native_bridge/actions'
import postMessage from '@@/native_bridge/post_message'
import { useGlobalConfirmationDialogStore } from '@@/pinia/global_confirmation_dialog'
import type { GlobalSnackbarNotification } from '@@/pinia/global_snackbar'
import {
  mapSnackbarToAppNotification,
  SnackbarNotificationType,
  useGlobalSnackbarStore,
} from '@@/pinia/global_snackbar'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostActionStore } from '@@/pinia/surface_post_action'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { Post, Wall } from '@@/types'
import { usePostSorter } from '@@/vuecomposables/surface_post_sorter'
import { createSharedComposable } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'

const usePostDuplicateAndTransfer = createSharedComposable(
  (): {
    duplicatePost: typeof duplicatePost
    transferPost: typeof transferPost
    canPostBeCopiedOrTransferred: typeof canPostBeCopiedOrTransferred
    checkIfPostCanBeCopiedOrTransferred: typeof checkIfPostCanBeCopiedOrTransferred
    isCopyingPost: typeof isCopyingPost
    isTransferringPost: typeof isTransferringPost
  } => {
    const surfacePostsStore = useSurfacePostsStore()
    const surfacePostActionStore = useSurfacePostActionStore()
    const globalConfirmationDialogStore = useGlobalConfirmationDialogStore()
    const { copyOrTransferPostResult, xCopyPostPanel, xTransferPostPanel } = storeToRefs(surfacePostActionStore)
    const { allSortedPostsByGroup } = storeToRefs(surfacePostsStore)
    const {
      isGroupByNone,
      isGroupBySection,
      isGroupByAuthor,
      isGroupByColor,
      isGroupBySingleSelect,
      customPropIdFromWishGroupBy,
    } = storeToRefs(useSurfaceWishArrangementStore())
    const postSorter = usePostSorter()

    const canPostBeCopiedOrTransferred = ref(false)
    const isTransferringPost = computed(() => canPostBeCopiedOrTransferred.value && xTransferPostPanel.value)
    const isCopyingPost = computed(() => canPostBeCopiedOrTransferred.value && xCopyPostPanel.value)
    const postUnderIntent = computed(() =>
      xCopyPostPanel.value ? surfacePostActionStore.postBeingCopied : surfacePostActionStore.postBeingTransferred,
    )

    const getPostsInSameGroup = (post: Post): Post[] => {
      let targetGroupKey: string | number | undefined
      if (isGroupByNone.value) {
        targetGroupKey = WallWishGroupByTypes.None
      } else if (isGroupBySection.value) {
        targetGroupKey = post.wall_section_id
      } else if (isGroupByColor.value) {
        targetGroupKey = post.color ?? 'default'
      } else if (isGroupByAuthor.value && post.author_hashid != null) {
        targetGroupKey = removeHashidPrefix(post.author_hashid)
      } else if (isGroupBySingleSelect.value && customPropIdFromWishGroupBy.value != null) {
        targetGroupKey =
          (post.custom_properties?.[customPropIdFromWishGroupBy.value] as string | undefined) ??
          WallWishGroupByValue.NoValue
      }

      if (targetGroupKey == null) return []

      return allSortedPostsByGroup.value[targetGroupKey] ?? []
    }

    const getNextPostInGroup = (post: Post, postsInGroup: Post[]): Post | undefined => {
      const currentPostIndex = postsInGroup.findIndex((p) => p.cid === post.cid)
      if (currentPostIndex === -1) return undefined
      return postsInGroup[currentPostIndex + 1] ?? undefined
    }

    const checkIfPostCanBeCopiedOrTransferred = (): void => {
      canPostBeCopiedOrTransferred.value = false

      if (postUnderIntent.value == null || (!xCopyPostPanel.value && !xTransferPostPanel.value)) {
        return
      }

      const currentPostAction = xCopyPostPanel.value ? 'copy' : 'transfer'
      if (hasCustomProperties(postUnderIntent.value)) {
        const title = __('Only matching custom fields will transfer.')
        const body = __(
          'Custom fields that have the same name and data type as the receiving padlet will be transferred. All other custom fields will be deleted.',
        )

        globalConfirmationDialogStore.openConfirmationDialog({
          iconSrc: ALERT_SIGN_EMOJI.iconSrc,
          title,
          body,
          afterConfirmActions: [
            () => {
              canPostBeCopiedOrTransferred.value = true
            },
          ],
          afterCancelActions: [
            () => {
              canPostBeCopiedOrTransferred.value = false
              currentPostAction === 'copy'
                ? surfacePostActionStore.endPostCopy()
                : surfacePostActionStore.endPostTransfer()
            },
          ],
        })
      } else {
        canPostBeCopiedOrTransferred.value = true
      }
    }

    const duplicatePost = (wall: Wall, groupingInfo?: PostGroupingInfo): void => {
      if (surfacePostActionStore.postBeingCopiedCid == null) return
      const post = surfacePostsStore.postEntitiesByCid[surfacePostActionStore.postBeingCopiedCid]
      if (post == null || post.hashid == null || post.id == null) return

      const isDestinationThisWall = post.wall_hashid === wall.hashid
      if (isDestinationThisWall) {
        trackEvent('Posts', 'Copied post to the same or an existing padlet')
      } else {
        trackEvent('Posts', 'Copied post to a new padlet')
      }

      // Get the next post in the same group (or if not grouped, the next post)
      const postsInSameGroup = getPostsInSameGroup(post)
      const nextPost = getNextPostInGroup(post, postsInSameGroup)
      const { sortIndex } = postSorter.getNewIndexBetween(post, nextPost)

      // If the destination wall is the same wall, we need to set the sort index as we want the
      // copied post to be right after the original post (either grouped or not grouped)
      // If we are copying to a different wall, it's fine to just append to the "New Post Position" of that wall
      surfacePostActionStore.actuallyCopyPost({
        wallHashid: wall.hashid,
        isDestinationThisWall,
        postId: post.id,
        postHashid: post.hashid,
        transferredPostCid: surfacePostActionStore.postBeingCopiedCid,
        transferCustomPropValuesAcrossWalls: !isDestinationThisWall,
        ...(isDestinationThisWall ? { sortIndex } : {}),
        groupingInfo,
      })

      surfacePostActionStore.endPostCopy()
    }

    const transferPost = (wall: Wall, groupingInfo?: PostGroupingInfo): void => {
      if (surfacePostActionStore.postBeingTransferredCid == null) return
      const post = surfacePostsStore.postEntitiesByCid[surfacePostActionStore.postBeingTransferredCid]
      if (post == null || post.hashid == null || post.id == null) return

      const isDestinationThisWall = post.wall_hashid === wall.hashid
      if (isDestinationThisWall) {
        // cannot transfer post to the same wall
        return
      } else {
        trackEvent('Posts', 'Transferred post to a new padlet')
      }
      surfacePostActionStore.actuallyTransferPost({
        wallHashid: wall.hashid,
        isDestinationThisWall,
        postId: post.id,
        postHashid: post.hashid,
        transferredPostCid: surfacePostActionStore.postBeingTransferredCid,
        transferCustomPropValuesAcrossWalls: !isDestinationThisWall,
        groupingInfo,
      })
      surfacePostActionStore.endPostTransfer()
    }

    if (isAppUsing('padletPickerV2')) {
      watch(copyOrTransferPostResult, (newResult: any) => {
        if (newResult == null) return
        let notification: GlobalSnackbarNotification = { message: newResult.text }

        if (newResult.status === 'error') {
          notification.notificationType = SnackbarNotificationType.error
        } else {
          if (newResult.isDestinationThisWall !== true) {
            notification = {
              ...notification,
              actionTextActions: [() => navigateTo(newResult.url)],
              actionText: __('Go to padlet'),
            }
          }
        }

        if (device.app && isAppCapableOf('nativeBridgeAppNotification')) {
          postMessage(showAppNotification(mapSnackbarToAppNotification(notification)))
          return
        }

        useGlobalSnackbarStore().setSnackbar(notification)
      })

      watch(
        [xCopyPostPanel, xTransferPostPanel, postUnderIntent],
        () => {
          checkIfPostCanBeCopiedOrTransferred()
        },
        { immediate: true },
      )
    }

    return {
      duplicatePost,
      transferPost,

      canPostBeCopiedOrTransferred,
      checkIfPostCanBeCopiedOrTransferred,

      isCopyingPost,
      isTransferringPost,
    }
  },
)

export default usePostDuplicateAndTransfer
